import { Button, Stack } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';

import { MesFaturamentoAutoComplete, StoreAutoComplete, TipoGradeAutoComplete } from 'components/autocompletes';
import { CissTextField } from 'components/fields';
import { ContentModal } from 'components/modal';
import { RequestGradeModelProps } from 'pages/logistica/grade/types';

interface GradeModalProps {
    open: boolean;
    setOpen: (action: boolean) => void;
    onClose?: () => void;
    grade?: RequestGradeModelProps | null;
    onSubmit: (grade: RequestGradeModelProps, formik: FormikHelpers<RequestGradeModelProps>) => void;
    isLoading?: boolean;
}

const getDateIsoString = (date?: Date | string | null): string => {
    let dateNow = new Date();

    if (date && typeof date === 'string') {
        dateNow = new Date(date);
    } else if (date && date !== 'string') {
        dateNow = date as Date;
    }

    const newDate = new Date(dateNow.valueOf() - dateNow.getTimezoneOffset() * 60000);
    return newDate.toISOString().replace(/\.\d{3}Z$/, '');
};

export function GradeModal({ open, setOpen, grade = null, isLoading = false, onClose = () => {}, onSubmit = () => {} }: GradeModalProps) {
    const validationNewGrade = Yup.object({
        loja: Yup.object().required(),
        tipoGrade: Yup.object().required(),
        dtColeta: Yup.string().required(),
        dtEntrega: Yup.string().required(),
        dtLimitePedido: Yup.string().required(),
        dhInicioEntrega: Yup.string().required(),
        dhFimEntrega: Yup.string().required(),
        dsObservacao: Yup.string().nullable(),
        mesFaturamento: Yup.string().required(),
    });

    return (
        <ContentModal closeButton open={open} setOpen={setOpen} sx={{ p: 5, width: 500, borderRadius: 1, maxWidth: '90%' }} onClose={onClose}>
            <Formik
                initialValues={
                    grade || {
                        dtColeta: getDateIsoString(),
                        dtEntrega: getDateIsoString(),
                        dtLimitePedido: getDateIsoString(),
                        dhInicioEntrega: getDateIsoString(),
                        dhFimEntrega: getDateIsoString(),
                        dsObservacao: '',
                    }
                }
                onSubmit={(values, formik) => onSubmit(values, formik)}
                validationSchema={validationNewGrade}
            >
                {(formik: FormikProps<RequestGradeModelProps>) => (
                    <Stack direction="column" sx={{ gap: 2 }}>
                        <StoreAutoComplete
                            value={formik.values.loja}
                            onChange={(e, value) => formik.setFieldValue('loja', value)}
                            error={formik.errors.loja}
                        />

                        <TipoGradeAutoComplete
                            value={formik.values.tipoGrade}
                            onChange={(e, value) => formik.setFieldValue('tipoGrade', value)}
                            error={formik.errors.tipoGrade}
                        />

                        <MesFaturamentoAutoComplete
                            value={formik.values.mesFaturamento || null}
                            error={formik.errors.mesFaturamento}
                            onChange={(e, value) => formik.setFieldValue('mesFaturamento', value)}
                        />

                        <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                            <DatePicker
                                format="dd/MM/yyyy"
                                value={formik.values.dtLimitePedido ? new Date(formik.values.dtLimitePedido) : null}
                                onChange={(value) => formik.setFieldValue('dtLimitePedido', value?.toISOString())}
                                slotProps={{
                                    textField: { label: 'Data limite do pedido', error: formik.errors.dtLimitePedido as any },
                                }}
                            />

                            <DatePicker
                                format="dd/MM/yyyy"
                                value={formik.values.dtColeta ? new Date(formik.values.dtColeta) : null}
                                onChange={(value) => formik.setFieldValue('dtColeta', value?.toISOString())}
                                slotProps={{
                                    textField: { label: 'Data de coleta', error: formik.errors.dtColeta as any },
                                }}
                            />

                            <DatePicker
                                format="dd/MM/yyyy"
                                value={formik.values.dtEntrega ? new Date(formik.values.dtEntrega) : null}
                                onChange={(value) => formik.setFieldValue('dtEntrega', value?.toISOString())}
                                slotProps={{
                                    textField: { label: 'Data de entrega', error: formik.errors.dtEntrega as any },
                                }}
                            />

                            <TimePicker
                                value={formik.values.dhInicioEntrega ? new Date(formik.values.dhInicioEntrega) : null}
                                onChange={(value) => formik.setFieldValue('dhInicioEntrega', value?.toISOString())}
                                slotProps={{
                                    textField: { label: 'Hora de inicio da entrega', error: formik.errors.dhInicioEntrega as any },
                                }}
                            />

                            <TimePicker
                                value={formik.values.dhFimEntrega ? new Date(formik.values.dhFimEntrega) : null}
                                onChange={(value) => formik.setFieldValue('dhFimEntrega', value?.toISOString())}
                                slotProps={{
                                    textField: {
                                        label: 'Hora de fim da entrega',
                                        error: Boolean(formik.errors.dhFimEntrega),
                                        helperText: formik.errors.dhFimEntrega,
                                    },
                                }}
                            />
                        </LocalizationProvider>

                        <CissTextField
                            label={'Observação'}
                            value={formik.values.dsObservacao}
                            onChange={(e) => formik.setFieldValue('dsObservacao', e.target.value)}
                            error={formik.errors.dsObservacao}
                        />

                        <Button variant="contained" size="large" fullWidth onClick={() => formik.submitForm()} loading={isLoading}>
                            {grade ? 'Atualizar' : 'Adicionar'}
                        </Button>
                    </Stack>
                )}
            </Formik>
        </ContentModal>
    );
}
